import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { graphql } from '@/gql';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { SortableList } from '@/components/ui/SortableList';
import { FormField } from '@/components/ui/FormLayout';

const LinksDoc = graphql(/* GraphQL */ `
  query Links {
    links {
      id
      name
      url
      sort
    }
  }
`);

const CreateLinkDoc = graphql(/* GraphQL */ `
  mutation CreateLink($input: CreateLinkInput!) {
    createLink(input: $input) {
      id
      name
      url
    }
  }
`);

const UpdateLinkDoc = graphql(/* GraphQL */ `
  mutation UpdateLink($id: ID!, $input: UpdateLinkInput!) {
    updateLink(id: $id, input: $input) {
      id
      name
      url
    }
  }
`);

const DeleteLinkDoc = graphql(/* GraphQL */ `
  mutation DeleteLink($id: ID!) {
    deleteLink(id: $id)
  }
`);

const ReorderLinksDoc = graphql(/* GraphQL */ `
  mutation ReorderLinks($entity: SortableEntity!, $orderedIds: [ID!]!) {
    reorder(entity: $entity, orderedIds: $orderedIds)
  }
`);

interface LinkRow {
  id: string;
  name: string;
  url: string;
  sort: number;
}

export function LinksListPage() {
  const { data, loading, refetch } = useQuery(LinksDoc);
  const [createLink, { loading: creating }] = useMutation(CreateLinkDoc);
  const [updateLink, { loading: updating }] = useMutation(UpdateLinkDoc);
  const [deleteLink, { loading: deleting }] = useMutation(DeleteLinkDoc);
  const [reorder] = useMutation(ReorderLinksDoc);

  const [editing, setEditing] = useState<LinkRow | 'new' | null>(null);
  const [formName, setFormName] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [pendingDelete, setPendingDelete] = useState<LinkRow | null>(null);

  const links: LinkRow[] = useMemo(
    () =>
      (data?.links ?? []).map((l) => ({
        id: l.id,
        name: l.name,
        url: l.url,
        sort: l.sort,
      })),
    [data],
  );

  const openNew = () => {
    setFormName('');
    setFormUrl('');
    setEditing('new');
  };
  const openEdit = (row: LinkRow) => {
    setFormName(row.name);
    setFormUrl(row.url);
    setEditing(row);
  };

  const saving = creating || updating;
  const submitForm = async () => {
    if (saving) return;
    if (!formName.trim() || !formUrl.trim()) return;
    if (editing === 'new') {
      await createLink({
        variables: { input: { name: formName.trim(), url: formUrl.trim() } },
      });
    } else if (editing) {
      await updateLink({
        variables: {
          id: editing.id,
          input: { name: formName.trim(), url: formUrl.trim() },
        },
      });
    }
    setEditing(null);
    await refetch();
  };

  const handleReorder = async (orderedIds: string[]) => {
    await reorder({ variables: { entity: 'LINK', orderedIds } });
    await refetch();
  };

  const confirmDelete = async () => {
    if (!pendingDelete || deleting) return;
    await deleteLink({ variables: { id: pendingDelete.id } });
    setPendingDelete(null);
    await refetch();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Links
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Quick links surfaced inside the POS. Drag to reorder.
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openNew}>
          New link
        </Button>
      </div>

      <Card padded={false}>
        {loading && links.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">Loading links…</p>
        ) : links.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">
            No links yet. Create one to surface it inside the POS.
          </p>
        ) : (
          <div className="p-4">
            <SortableList
              items={links}
              onReorder={handleReorder}
              renderItem={(item, { dragHandle }) => (
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-gray-900">
                  {dragHandle}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900 dark:text-white">
                      {item.name}
                    </p>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-brand dark:text-gray-400"
                    >
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      {item.url}
                    </a>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Pencil className="h-4 w-4" />}
                    onClick={() => openEdit(item)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Trash2 className="h-4 w-4 text-red-500" />}
                    onClick={() => setPendingDelete(item)}
                  >
                    Delete
                  </Button>
                </div>
              )}
            />
          </div>
        )}
      </Card>

      <Modal
        open={editing !== null}
        onClose={() => (saving ? null : setEditing(null))}
        title={editing === 'new' ? 'New link' : 'Edit link'}
        footer={
          <>
            <Button
              variant="ghost"
              type="button"
              onClick={() => setEditing(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitForm}
              disabled={saving || !formName.trim() || !formUrl.trim()}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Name" required>
            <Input
              value={formName}
              onChange={(event) => setFormName(event.target.value)}
              required
            />
          </FormField>
          <FormField label="URL" required>
            <Input
              type="url"
              value={formUrl}
              onChange={(event) => setFormUrl(event.target.value)}
              placeholder="https://…"
              required
            />
          </FormField>
        </div>
      </Modal>

      <Modal
        open={pendingDelete !== null}
        onClose={() => (deleting ? null : setPendingDelete(null))}
        title="Delete link"
        footer={
          <>
            <Button
              variant="ghost"
              type="button"
              onClick={() => setPendingDelete(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              type="button"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </>
        }
      >
        <p>
          Delete <strong>{pendingDelete?.name}</strong>? This cannot be undone.
        </p>
      </Modal>
    </div>
  );
}
